/*
 * Carbisdale - SingleLineInput
 *
 * Wiederverwendbare einzeilige Text-Eingabe-Komponente.
 * Verwaltet Text, Cursor, Selection und horizontales Scrolling.
 * Rendering-agnostisch — gibt State zurück, den der Aufrufer rendert.
 *
 * Einsatzbereiche: Separator-Titel, Suche (zukünftig).
 */

/**
 * Einzeilige Text-Eingabe mit Cursor, Selection und Scroll.
 */
export class SingleLineInput {
    /**
     * @param {Object} options
     * @param {Function} [options.onChange] - Aufgerufen bei Textänderung (text)
     * @param {Function} [options.onEnter] - Aufgerufen bei Enter
     * @param {Function} [options.onTab] - Aufgerufen bei Tab
     * @param {Function} [options.onShiftTab] - Aufgerufen bei Shift+Tab
     * @param {Function} [options.onNavigateOut] - Aufgerufen bei ArrowUp/Down ('up'|'down')
     */
    constructor(options = {}) {
        this.text = ''
        this.cursorPos = 0
        this.selectionStart = null  // null = keine Selection
        this.selectionEnd = null
        this.scrollOffset = 0       // Zeichen-Offset für horizontales Scrolling

        this._visibleWidth = 40     // Sichtbare Breite in Zeichen (vom Aufrufer gesetzt)
        this._isDragging = false

        this._onChange = options.onChange || (() => {})
        this._onEnter = options.onEnter || (() => {})
        this._onTab = options.onTab || (() => {})
        this._onShiftTab = options.onShiftTab || (() => {})
        this._onNavigateOut = options.onNavigateOut || (() => {})
    }

    // ===== Public API =====

    /**
     * Setzt den Text und positioniert Cursor ans Ende.
     * @param {string} text
     */
    setText(text) {
        this.text = text || ''
        this.cursorPos = this.text.length
        this.selectionStart = null
        this.selectionEnd = null
        this.scrollOffset = 0
        this._ensureCursorVisible()
    }

    /**
     * Gibt den aktuellen Text zurück.
     * @returns {string}
     */
    getText() {
        return this.text
    }

    /**
     * Setzt die sichtbare Breite in Zeichen.
     * @param {number} width
     */
    setVisibleWidth(width) {
        this._visibleWidth = Math.max(1, width)
        this._ensureCursorVisible()
    }

    /**
     * Gibt true zurück wenn eine Selection aktiv ist.
     * @returns {boolean}
     */
    hasSelection() {
        return this.selectionStart !== null && this.selectionStart !== this.selectionEnd
    }

    /**
     * Gibt den selektierten Text zurück.
     * @returns {string}
     */
    getSelectedText() {
        if (!this.hasSelection()) return ''
        const start = Math.min(this.selectionStart, this.selectionEnd)
        const end = Math.max(this.selectionStart, this.selectionEnd)
        return this.text.slice(start, end)
    }

    /**
     * Gibt den normalisierten Selection-Bereich zurück.
     * @returns {{start: number, end: number}|null}
     */
    getSelectionRange() {
        if (!this.hasSelection()) return null
        return {
            start: Math.min(this.selectionStart, this.selectionEnd),
            end: Math.max(this.selectionStart, this.selectionEnd)
        }
    }

    /**
     * Verarbeitet einen Tastendruck.
     * @param {KeyboardEvent} e
     * @returns {boolean} true wenn Event behandelt wurde
     */
    handleKeydown(e) {
        const key = e.key
        const isMeta = e.metaKey || e.ctrlKey
        const isShift = e.shiftKey

        // Modifier-Only-Tasten ignorieren
        if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(key)) {
            return false
        }

        // Escape nicht schlucken — Aufrufer (z.B. Pane-Fullscreen-Toggle) soll reagieren
        if (key === 'Escape') {
            return false
        }

        // Navigation raus: ArrowUp / ArrowDown
        if (key === 'ArrowUp' && !isShift) {
            this._onNavigateOut('up')
            return true
        }
        if (key === 'ArrowDown' && !isShift) {
            this._onNavigateOut('down')
            return true
        }

        // Tab / Shift+Tab
        if (key === 'Tab') {
            if (isShift) {
                this._onShiftTab()
            } else {
                this._onTab()
            }
            return true
        }

        // Enter
        if (key === 'Enter') {
            this._onEnter()
            return true
        }

        // Cmd+A: Alles selektieren
        if (isMeta && key === 'a') {
            this.selectionStart = 0
            this.selectionEnd = this.text.length
            this.cursorPos = this.text.length
            return true
        }

        // Cmd+C: Kopieren — wird vom Aufrufer gehandhabt (getSelectedText)
        if (isMeta && key === 'c') {
            return false  // Durchlassen für Browser-Copy
        }

        // Cmd+X: Ausschneiden — wird vom Aufrufer gehandhabt
        if (isMeta && key === 'x') {
            if (this.hasSelection()) {
                // Text wird vom Aufrufer aus der Clipboard gelesen
                this._deleteSelection()
                this._onChange(this.text)
            }
            return false  // Durchlassen für Browser-Cut
        }

        // Backspace
        if (key === 'Backspace') {
            if (this.hasSelection()) {
                this._deleteSelection()
            } else if (this.cursorPos > 0) {
                // Cmd+Backspace: Alles vor dem Cursor löschen
                if (isMeta) {
                    this.text = this.text.slice(this.cursorPos)
                    this.cursorPos = 0
                } else {
                    this.text = this.text.slice(0, this.cursorPos - 1) + this.text.slice(this.cursorPos)
                    this.cursorPos--
                }
            }
            this._ensureCursorVisible()
            this._onChange(this.text)
            return true
        }

        // Delete
        if (key === 'Delete') {
            if (this.hasSelection()) {
                this._deleteSelection()
            } else if (this.cursorPos < this.text.length) {
                this.text = this.text.slice(0, this.cursorPos) + this.text.slice(this.cursorPos + 1)
            }
            this._ensureCursorVisible()
            this._onChange(this.text)
            return true
        }

        // ArrowLeft
        if (key === 'ArrowLeft') {
            if (isShift) {
                this._extendSelection('left', isMeta)
            } else {
                if (this.hasSelection()) {
                    // Bei aktiver Selection: Cursor an den Anfang der Selection
                    this.cursorPos = Math.min(this.selectionStart, this.selectionEnd)
                    this._clearSelection()
                } else if (isMeta) {
                    this.cursorPos = 0
                } else if (this.cursorPos > 0) {
                    this.cursorPos--
                } else {
                    // Am Anfang: zum vorherigen Absatz navigieren
                    this._onNavigateOut('left')
                    return true
                }
            }
            this._ensureCursorVisible()
            return true
        }

        // ArrowRight
        if (key === 'ArrowRight') {
            if (isShift) {
                this._extendSelection('right', isMeta)
            } else {
                if (this.hasSelection()) {
                    // Bei aktiver Selection: Cursor ans Ende der Selection
                    this.cursorPos = Math.max(this.selectionStart, this.selectionEnd)
                    this._clearSelection()
                } else if (isMeta) {
                    this.cursorPos = this.text.length
                } else if (this.cursorPos < this.text.length) {
                    this.cursorPos++
                } else {
                    // Am Ende: zum nächsten Absatz navigieren
                    this._onNavigateOut('right')
                    return true
                }
            }
            this._ensureCursorVisible()
            return true
        }

        // Home
        if (key === 'Home') {
            if (isShift) {
                this._extendSelection('home')
            } else {
                this.cursorPos = 0
                this._clearSelection()
            }
            this._ensureCursorVisible()
            return true
        }

        // End
        if (key === 'End') {
            if (isShift) {
                this._extendSelection('end')
            } else {
                this.cursorPos = this.text.length
                this._clearSelection()
            }
            this._ensureCursorVisible()
            return true
        }

        // Druckbares Zeichen einfügen
        // Alt (Option) auf dem Mac erzeugt Sonderzeichen (@, €, {, }, etc.) — nicht blockieren
        if (key.length === 1 && !isMeta) {
            this._insertText(key)
            return true
        }

        // Alle anderen Tasten abfangen (damit sie nicht durchrutschen)
        return true
    }

    /**
     * Verarbeitet Paste-Events. Newlines werden entfernt.
     * @param {string} text
     */
    handlePaste(text) {
        // Newlines und Format-Marker entfernen
        const cleaned = text.replace(/\r\n/g, ' ').replace(/[\r\n]/g, ' ').trim()
        if (cleaned.length > 0) {
            this._insertText(cleaned)
        }
    }

    /**
     * Maus-Klick: Cursor setzen.
     * @param {number} charOffset - Zeichen-Position im Text (0-basiert)
     */
    handleMouseDown(charOffset) {
        this.cursorPos = this._clampOffset(charOffset + this.scrollOffset)
        this._clearSelection()
        this._isDragging = true
        this.selectionStart = this.cursorPos
        this.selectionEnd = this.cursorPos
    }

    /**
     * Maus-Drag: Selection erweitern.
     * @param {number} charOffset - Zeichen-Position im sichtbaren Bereich
     */
    handleMouseMove(charOffset) {
        if (!this._isDragging) return
        this.cursorPos = this._clampOffset(charOffset + this.scrollOffset)
        this.selectionEnd = this.cursorPos
        this._ensureCursorVisible()
    }

    /**
     * Maus loslassen: Drag beenden.
     */
    handleMouseUp() {
        this._isDragging = false
        // Leere Selection entfernen
        if (this.selectionStart === this.selectionEnd) {
            this._clearSelection()
        }
    }

    /**
     * Gibt den aktuellen Zustand zum Rendern zurück.
     * @returns {{text: string, cursorPos: number, scrollOffset: number, selectionStart: number|null, selectionEnd: number|null}}
     */
    getState() {
        return {
            text: this.text,
            cursorPos: this.cursorPos,
            scrollOffset: this.scrollOffset,
            selectionStart: this.selectionStart,
            selectionEnd: this.selectionEnd
        }
    }

    // ===== Private =====

    /**
     * Fügt Text an der Cursor-Position ein. Löscht vorher die Selection.
     * @param {string} str
     */
    _insertText(str) {
        if (this.hasSelection()) {
            this._deleteSelection()
        }
        this.text = this.text.slice(0, this.cursorPos) + str + this.text.slice(this.cursorPos)
        this.cursorPos += str.length
        this._ensureCursorVisible()
        this._onChange(this.text)
    }

    /**
     * Löscht die aktive Selection und setzt den Cursor an den Anfang.
     */
    _deleteSelection() {
        if (!this.hasSelection()) return
        const start = Math.min(this.selectionStart, this.selectionEnd)
        const end = Math.max(this.selectionStart, this.selectionEnd)
        this.text = this.text.slice(0, start) + this.text.slice(end)
        this.cursorPos = start
        this._clearSelection()
        this._ensureCursorVisible()
    }

    /**
     * Erweitert die Selection in die angegebene Richtung.
     * @param {'left'|'right'|'home'|'end'} direction
     * @param {boolean} [toEdge] - Bis zum Rand (Cmd+Shift+Arrow)
     */
    _extendSelection(direction, toEdge = false) {
        // Selection starten falls nötig
        if (this.selectionStart === null) {
            this.selectionStart = this.cursorPos
            this.selectionEnd = this.cursorPos
        }

        switch (direction) {
        case 'left':
            if (toEdge) {
                this.cursorPos = 0
            } else if (this.cursorPos > 0) {
                this.cursorPos--
            }
            break
        case 'right':
            if (toEdge) {
                this.cursorPos = this.text.length
            } else if (this.cursorPos < this.text.length) {
                this.cursorPos++
            }
            break
        case 'home':
            this.cursorPos = 0
            break
        case 'end':
            this.cursorPos = this.text.length
            break
        }

        this.selectionEnd = this.cursorPos

        // Leere Selection entfernen
        if (this.selectionStart === this.selectionEnd) {
            this._clearSelection()
        }
    }

    /**
     * Selection aufheben.
     */
    _clearSelection() {
        this.selectionStart = null
        this.selectionEnd = null
    }

    /**
     * Stellt sicher, dass der Cursor im sichtbaren Bereich liegt.
     * Passt scrollOffset an wenn nötig.
     */
    _ensureCursorVisible() {
        if (this.cursorPos < this.scrollOffset) {
            this.scrollOffset = this.cursorPos
        } else if (this.cursorPos > this.scrollOffset + this._visibleWidth) {
            this.scrollOffset = this.cursorPos - this._visibleWidth
        }
        // scrollOffset nie negativ
        if (this.scrollOffset < 0) this.scrollOffset = 0
    }

    /**
     * Begrenzt einen Offset auf den gültigen Bereich.
     * @param {number} offset
     * @returns {number}
     */
    _clampOffset(offset) {
        return Math.max(0, Math.min(this.text.length, offset))
    }
}
